// WORK LANDING (brief §5.1; prototype docs/design/work-hifi landingView): the Workflow Definitions
// list — the Work section's front door. It fetches the landing projection through the injected Work
// transport and renders it natively (tokens only, G-181): the derived stat strip, one row per
// definition opening its BOARD route via the onOpenBoard callback (navigation is callbacks-only —
// the surface never imports shell state), the role-dependent composition footer (operator → the
// Composer via onOpenComposer; manager → the request panel), and the one-inbox note (N-APPROVE-1).
// Every displayed figure derives from the projection (G-182); rows carry NO verdict control —
// verdicts live inside the opened card (brief §5.2). Honest states render via SurfaceFrame (P-SELF).

import { useWorkApi } from '../api-client.ts';
import type { WorkLandingProjection } from '../projections.ts';
import type { WorkLandingSurfaceProps } from '../surface-contracts.ts';
import { useProjection } from '../../useProjection.ts';
import { SurfaceFrame } from '../../surface-frame.tsx';
import { DefinitionList } from './components/DefinitionList.tsx';
import { LandingFooter } from './components/LandingFooter.tsx';
import { LandingStats } from './components/LandingStats.tsx';

/** The Work landing surface (the `landing` WorkRoute). */
export function WorkLandingSurface({
  role,
  onOpenBoard,
  onOpenComposer,
  onRequestWorkflow,
}: WorkLandingSurfaceProps): React.JSX.Element {
  const api = useWorkApi();
  const state = useProjection<WorkLandingProjection>(api.getLanding);

  return (
    <div>
      <div className="mb-6">
        <div className="ritona-numeric text-xs uppercase tracking-eyebrow text-forest">
          Work · operating
        </div>
        <h1 className="ritona-voice mt-2 text-2xl font-medium tracking-snug text-text">
          Workflows
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-text-muted">
          Every live instance of a repeating process, worked up to the point of a decision and
          frozen there. One sweep clock runs each on its cadence; the decisions wait for you.
        </p>
      </div>
      <SurfaceFrame state={state}>
        {(landing) => (
          <div>
            <LandingStats definitions={landing.definitions} />
            <section aria-label="Workflows">
              <DefinitionList definitions={landing.definitions} onOpenBoard={onOpenBoard} />
            </section>
            <LandingFooter
              role={role}
              onOpenComposer={onOpenComposer}
              onRequestWorkflow={onRequestWorkflow}
            />
          </div>
        )}
      </SurfaceFrame>
    </div>
  );
}
